import { Arrays, asDefined, Errors, isDefined, MutableObservableOption, panic, RuntimeNotifier } from "@opendaw/lib-std";
import { Promises } from "@opendaw/lib-runtime";
export var Files;
(function (Files) {
    Files.save = async (arrayBuffer, options) => {
        if (isDefined(window.showSaveFilePicker)) {
            const { status, error, value: handle } = await Promises.tryCatch(window.showSaveFilePicker(options));
            if (status === "rejected") {
                if (Errors.isAbort(error)) {
                    return Promise.reject(error);
                }
                if (Errors.isNotAllowed(error)) {
                    return saveBlobFallback(arrayBuffer, options);
                }
                return RuntimeNotifier.info({
                    headline: "Could not show file picker",
                    message: String(error)
                }).then(() => Promise.reject(error));
            }
            const writeFile = async () => {
                const writable = await handle.createWritable();
                await writable.truncate(0);
                await writable.write(arrayBuffer);
                await writable.close();
            };
            const { status: writeStatus, error: writeError } = await Promises.tryCatch(writeFile());
            if (writeStatus === "rejected") {
                console.warn(writeError);
                RuntimeNotifier.notify({ message: "Could not save file.", icon: "Warning" });
                return Promise.reject(writeError);
            }
            return handle.name ?? "unknown";
        }
        else {
            return saveBlobFallback(arrayBuffer, options);
        }
    };
    // browsers need a user-input to allow download
    Files.saveWithApproval = async ({ buffer, headline, message, suggestedName, types }) => {
        const approved = await RuntimeNotifier.approve({ headline, message: message ?? "", approveText: "Save" });
        if (!approved) {
            return;
        }
        const { status, error } = await Promises.tryCatch(Files.save(buffer, { suggestedName, types: types?.slice() }));
        if (status === "rejected" && !Errors.isAbort(error)) {
            panic(String(error));
        }
    };
    const saveBlobFallback = (arrayBuffer, options) => {
        const blob = new Blob([arrayBuffer]);
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = options?.suggestedName ?? `unknown`;
        anchor.click();
        URL.revokeObjectURL(url);
        return options?.suggestedName ?? "Unknown";
    };
    const pendingOpen = new MutableObservableOption();
    // browsers allow one native picker at a time; a re-entrant call is an abort, not an error
    Files.open = (options) => {
        if (pendingOpen.nonEmpty()) {
            return Promise.reject(Errors.AbortError);
        }
        const promise = openPicker(options).finally(() => pendingOpen.clear());
        pendingOpen.wrap(promise);
        return promise;
    };
    const openPicker = async (options) => {
        if (isDefined(window.showOpenFilePicker)) {
            const { status, value: fileHandles, error } = await Promises.tryCatch(window.showOpenFilePicker(options));
            if (status === "rejected") {
                return Promise.reject(error);
            }
            return Promise.all(fileHandles.map(fileHandle => fileHandle.getFile()));
        }
        else {
            return new Promise((resolve, reject) => {
                if (isDefined(options)) {
                    console.warn("FileApi.showOpenFilePicker is emulated in this browser. OpenFilePickerOptions are ignored.");
                }
                const fileInput = document.createElement("input");
                fileInput.type = "file";
                fileInput.multiple = options?.multiple ?? false;
                fileInput.style.display = "none";
                fileInput.addEventListener("cancel", async () => {
                    fileInput.remove();
                    reject(new DOMException("cancel", "AbortError"));
                });
                fileInput.addEventListener("change", async (event) => {
                    const target = event.target;
                    const files = target.files;
                    if (isDefined(files)) {
                        resolve(Arrays.create(index => asDefined(files.item(index), `No file at index ${index}`), files.length));
                    }
                    else {
                        reject(new DOMException("cancel", "AbortError"));
                    }
                    fileInput.remove();
                });
                document.body.appendChild(fileInput);
                fileInput.click();
            });
        }
    };
})(Files || (Files = {}));
